import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import "./fmdo.scss";
import { API_BASE, PLAN_INFO, WHATSAPP_URL, SIGNUP_URL } from "./data";
import { loadMetaPixel, fbqTrack } from "./pixel";

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = RAZORPAY_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// Per-plan checkout page (ported from plan.html). Reached at /ads/plan/:plan?app=<id>
// only after the application form was saved; guards redirect back to /ads otherwise.
export default function PlanCheckout() {
  const { plan: slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const info = PLAN_INFO[slug];
  const applicationId = Number(searchParams.get("app"));
  const invalid = !info || !applicationId || Number.isNaN(applicationId);

  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [paymentId, setPaymentId] = useState(null);

  useEffect(() => {
    if (invalid) {
      navigate("/ads", { replace: true });
      return;
    }
    document.title = `FMDO – ${info.name} Checkout`;
    loadMetaPixel();
    if (info.cta === "pay") {
      fbqTrack("InitiateCheckout", { content_name: info.name, value: info.amount / 100, currency: "INR" });
      loadRazorpay();
    } else {
      fbqTrack("Contact");
    }
  }, [invalid, info, navigate]);

  useEffect(() => {
    document.body.classList.add("fmdo-body");
    document.documentElement.classList.add("fmdo-html");
    return () => {
      document.body.classList.remove("fmdo-body");
      document.documentElement.classList.remove("fmdo-html");
    };
  }, []);

  if (invalid) return null;

  async function handlePay() {
    setPaying(true);
    setError("");
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment gateway. Please check your connection.");

      const res = await fetch(`${API_BASE}/api/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: info.planKey, applicationId }),
      });
      const data = await res.json();
      if (!data.order_id) throw new Error(data.error || "Order creation failed");

      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "FMDO – FM Digital Official",
        description: info.name,
        order_id: data.order_id,
        theme: { color: info.accent },
        handler: async (response) => {
          setPaying(false);
          const vRes = await fetch(`${API_BASE}/api/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vData.verified) {
            fbqTrack("Purchase", { value: info.amount / 100, currency: data.currency || "INR", content_name: info.name });
            setPaymentId(response.razorpay_payment_id);
          } else {
            setError(`Payment received but verification failed. Please contact support with Payment ID: ${response.razorpay_payment_id}`);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", (response) => {
        setError(`Payment failed: ${response.error.description}. Please try again.`);
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not initiate payment. Please try again or contact support.");
      setPaying(false);
    }
  }

  const accentStyle = { "--accent": info.accent, "--accent-rgb": info.accentRgb };

  return (
    <div className="fmdo-checkout" style={accentStyle}>
      <div className="plan-card">
        <div className="plan-eyebrow">FMDO Checkout</div>
        <h1 className="plan-name">{info.name}</h1>
        <div className="plan-price">
          {info.price.per
            ? <><sup>₹</sup>{info.price.amount}<span>{info.price.per}</span></>
            : info.price.amount}
        </div>
        <p className="plan-note">{info.note}</p>
        <ul className="plan-features">
          {info.features.map((f) => <li key={f}>{f}</li>)}
        </ul>

        {error && <div className="plan-error">{error}</div>}

        {paymentId ? (
          <div className="success-block">
            <div className="success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p>Welcome aboard 🎉 Your payment is confirmed. Let's set up your account so you can start releasing your music.</p>
            <span className="success-pid">Payment ID: {paymentId}</span>
            <a className="plan-cta" href={SIGNUP_URL}>Continue to Sign Up →</a>
          </div>
        ) : info.cta === "whatsapp" ? (
          <a className="plan-cta" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Chat with us on WhatsApp →
          </a>
        ) : (
          <button className="plan-cta" type="button" disabled={paying} onClick={handlePay}>
            {paying ? "Processing…" : "Pay Now"}
          </button>
        )}

        <Link className="plan-back" to="/ads">← Back to FM Digital Official</Link>
      </div>
    </div>
  );
}
